import { useContext, useState, useEffect, useRef } from "react";
import { BiDotsHorizontalRounded, BiRecycle } from "react-icons/bi";
import { GridActionsCellItem } from "@mui/x-data-grid";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import DeleteModal from "../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../components/popovers/actionPopover";
import CustomStyledTable from "../../../../components/table/CustomStyledTable";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { EmployeeDto, GENDER, STATUS } from "../../../../models/employee.dto";
import ENDPOINTS from "../../../../services/utils/endpoints";
import EmployeeFormContextWrapper, {
  EmployeeFormContext,
} from "../EmployeeFormContext";
import {
  useDeleteEmployee,
  useGetEmployee,
  useGetEmployeeSearch,
  useReinstateEmployee,
} from "../../../../services/employee.service";
import APISearchInput from "../../../../components/search/APISearchInput";
import {
  useManageTableWithSearchParams,
  useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import { AiOutlinePlus } from "react-icons/ai";
import { GoUpload } from "react-icons/go";
import { Upload, ChevronDown } from "lucide-react";
import AddNewEmployee from "../AddNewEmployee";
import EditEmployee from "../EditEmployee";
import UploadEmployees from "../modals/UploadEmployees";
import UploadSalary from "../modals/UploadSalary";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import useUserStore from "../../../../state-management/useUserStore";
import { toast } from "react-toastify";
import { PATHS } from "../../../../routes/routes.paths";
import { useNavigate } from "react-router-dom";
import UpdateAccountConfigModal from "../../../../components/modals/UpdateAccountConfigModal";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import { RiIndeterminateCircleLine } from "react-icons/ri";
import { MdOutlineRestoreFromTrash } from "react-icons/md";
import TerminateEmployee from "../modals/TerminateEmployee";
import ReinstateEmployee from "../modals/ReinstateEmployee";
import API from "../../../../services/utils/api";
import { useAsyncEffect, useLockFn } from "ahooks";
import { useSearchParams } from "react-router-dom";
import { getAccessToken } from "../../../../services/utils/app.data";
import {
  PermissionChecker,
  PERMISSIONS,
  useCheckUserPermissions,
} from "../../../../helpers/usePermissionManager";
import ResendConfirmation from "../modals/ResendConfirmation";
import { BsDownload } from "react-icons/bs";
import ExportEmployeesModal from "../modals/ExportEmployeesModal";

import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import {
  tanstackEmployeeColumns,
  defaultColumnVisibility,
} from "./tanstack-columns";
import { useTableConfiguration } from "../../../../helpers/useTableConfiguration";
import { TableId } from "../../../../models/table-configuration.dto";

const EmployeesMainContent = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const newData = searchParams.get("newData");
  const { currentCompany } = useCompanyStore();
  const { currentUser, userType } = useUserStore();
  const userOrganization = currentUser.user.organization;
  const employeeFormContextData = useContext(EmployeeFormContext);
  const { checkUserPermissionsAnd } = useCheckUserPermissions();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openUploadEmployeesModal, setOpenUploadEmployeesModal] =
    useState(false);
  const [openUploadSalaryModal, setOpenUploadSalaryModal] = useState(false);
  const [openAddEmployeeModal, setOpenAddEmployeeModal] = useState(false);
  const [openEditEmployeeModal, setOpenEditEmployeeModal] = useState(false);
  const [openResendConfirmationModal, setOpenResendConfirmationModal] =
    useState(false);
  const [openTerminateEmployeeModal, setOpenTerminateEmployeeModal] =
    useState(false);
  const [openReinstateEmployeeModal, setOpenReinstateEmployeeModal] =
    useState(false);
  const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
  const [openExportEmployeesModal, setOpenExportEmployeesModal] =
    useState(false);
  const [totalOrganizationEmployees, setTotalOrganizationEmployees] =
    useState(0);
  const [checkingEmployeeLimit, setCheckingEmployeeLimit] = useState(false);
  const [upgradeModalContent, setUpgradeModalContent] = useState("");
  const [onClickActionButton, setOnClickActionButton] = useState<
    (() => void) | null
  >(null);
  const highestSubscriptionTierId = sessionStorage.getItem(
    "highestSubscriptionTierId",
  );
  const { tableKey, columnVisibilityModel, setColumnVisibilityModel } =
    useColumnVisibilityModel(defaultColumnVisibility);

  useAsyncEffect(
    useLockFn(async () => {
      try {
        const { data } = await API.ProcessingCompanyAPI.getEmployeeCount({
          organizationId: userOrganization!.id,
        });
        setTotalOrganizationEmployees(data?.count);
      } finally {
      }
    }),
    [newData],
  );

  const { mutate: searchEmployees } = useGetEmployeeSearch();
  const {
    tableAPIRef,
    fetchedTableData,
    tableData,
    isLoading,
    pageCount,
    setTableData,
  } = useManageTableWithSearchParams<EmployeeDto>({
    fetchHook: useGetEmployee,
    options: { companyId: currentCompany?.id as number, status: "ACTIVE" },
  });

  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleClose = () => setAnchorEl(null);

  // Re-fetches the org-wide employee count and returns it. Always hitting
  // the backend here — instead of trusting the count fetched once on mount —
  // is what stops a cancelled upgrade prompt from being bypassed by simply
  // clicking "Add New Employee" again with stale local state.
  const getFreshEmployeeCount = async (): Promise<number> => {
    try {
      const { data } = await API.ProcessingCompanyAPI.getEmployeeCount({
        organizationId: userOrganization!.id,
      });
      const count = data?.count ?? totalOrganizationEmployees;
      setTotalOrganizationEmployees(count);
      return count;
    } catch {
      return totalOrganizationEmployees;
    }
  };

  const showUpgradeGate = (currentTotal: number) => {
    if (userOrganization?.billingType.name.toLowerCase() === "contract") {
      if (checkUserPermissionsAnd([PERMISSIONS.SUBSCRIPTIONS_WRITE])) {
        setOpenUpgradeModal(true);
        setOnClickActionButton(null);
        setUpgradeModalContent(
          "Maximum employee limit reached! Kindly contact the Akatua team to increase limit.",
        );
      } else {
        toast.error("Max employees reached for this organization.");
        toast.error("Contact your organization admin to upgrade account.");
      }
      return;
    }

    if (highestSubscriptionTierId === userOrganization?.subsciptionTier.id) {
      if (checkUserPermissionsAnd([PERMISSIONS.SUBSCRIPTIONS_WRITE])) {
        setOpenUpgradeModal(true);
        setOnClickActionButton(null);
        setUpgradeModalContent(`Maximum employee limit reached! Kindly contact the Akatua team to increase 
					limit as you are currently on the highest subscription package.`);
      } else {
        toast.error("Max employees reached for this organization.");
        toast.error(
          "You do not have permission to upgrage this organization account.",
        );
      }
      return;
    }

    setOpenUpgradeModal(true);
    // Passing a function to useState is treated as an updater and invoked
    // immediately, so the callback itself must be wrapped in an extra
    // arrow function to be stored rather than executed here.
    setOnClickActionButton(
      () => () =>
        navigate(PATHS.CREATE_PAYROLL_COMPANY + "?upgradeRequired=true"),
    );
    setUpgradeModalContent(
      "Maximum employee limit reached! Upgrade to higher subscription package to continue.",
    );
  };

  const handleCreateEmployeeForm = async (setCreateEmployeeForm: any) => {
    if (userType !== "ORGANIZATION") {
      setCreateEmployeeForm(true);
      return;
    }

    if (!userOrganization?.config) {
      toast.info(
        "Organization configurations not found! Kindly sign out and sign back in to fix this.",
      );
      return;
    }

    if (checkingEmployeeLimit) return;
    setCheckingEmployeeLimit(true);
    const currentTotal = await getFreshEmployeeCount();
    setCheckingEmployeeLimit(false);

    const maxEmployees = userOrganization?.config.maxEmployees as number;

    if (maxEmployees < 0 || maxEmployees > currentTotal) {
      setCreateEmployeeForm(true);
      return;
    }

    showUpgradeGate(currentTotal);
  };

  const handleCreateSalaryForm = async (setCreateSalaryForm: any) => {
    if (userType !== "ORGANIZATION") {
      setCreateSalaryForm(true);
      return;
    }

    if (!userOrganization?.config) {
      toast.info(
        "Organization configurations not found! Kindly sign out and sign back in to fix this.",
      );
      return;
    }

    if (checkingEmployeeLimit) return;
    setCheckingEmployeeLimit(true);
    const currentTotal = await getFreshEmployeeCount();
    setCheckingEmployeeLimit(false);

    const maxEmployees = userOrganization?.config.maxEmployees as number;

    if (maxEmployees < 0 || maxEmployees > currentTotal) {
      setCreateSalaryForm(true);
      return;
    }

    showUpgradeGate(currentTotal);
  };

  const { formDataValues, setFormData } = useEditFormStore();
  const { reloadTableData } = useManageTableWithSearchParamsData();

  const additionalColumns = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      minWidth: 200,
      flex: 1,
      headerClassName: "super-app-theme--header",
      cellClassName: "actions ",
      getActions: (params: any) => {
        return [
          <GridActionsCellItem
            icon={<BiDotsHorizontalRounded />}
            label="actions"
            onClick={(e: any) => {
              setFormData(params?.row);
              setAnchorEl(e.currentTarget);
            }}
          />,
        ];
      },
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-bold text-xl">Employees</h1>
      </div>
      <EmployeeFormContextWrapper>
        <div className="mb-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <APISearchInput
              fallbackData={fetchedTableData}
              setTableData={setTableData}
              mutate={searchEmployees}
            />

            <TableFilter
              fields={[
                {
                  title: "",
                  fieldName: "",
                  fieldType: "SORT",
                  options: [
                    {
                      title: "First Name",
                      fieldName: "firstName",
                    },
                    {
                      title: "Last Name",
                      fieldName: "lastName",
                    },
                    {
                      title: "Date Created",
                      fieldName: "createdAt",
                    },
                    {
                      title: "Date Modified",
                      fieldName: "modifiedAt",
                    },
                  ],
                },
                {
                  title: "Gender",
                  fieldName: "gender",
                  fieldType: "CHECKBOX_LIST",
                  options: GENDER,
                },
                {
                  title: "HR",
                  fieldName: "hr",
                  fieldType: "YES_NO",
                },
                {
                  title: "Job Title",
                  fieldName: "jobTitleId",
                  fieldType: "SELECT_FIELD",
                  options: employeeFormContextData?.jobTitles || [],
                },
                {
                  title: "Major Grade Level",
                  fieldName: "majorGradeLevelId",
                  fieldType: "SELECT_FIELD",
                  options: employeeFormContextData?.MajorGradeLevelData || [],
                },
                {
                  title: "Minor Grade Level",
                  fieldName: "minorGradeLevelId",
                  fieldType: "SELECT_FIELD",
                  options: employeeFormContextData?.MinorGradeLevelData || [],
                },
                {
                  title: "Employee Status",
                  fieldName: "status",
                  fieldType: "CHECKBOX_LIST",
                  options: STATUS,
                },
                {
                  title: "Account Status",
                  fieldName: "userStatus",
                  fieldType: "CHECKBOX_LIST",
                  options: { ACTIVE: "ACTIVE", INACTIVE: "INACTIVE" },
                },
                {
                  title: "Date Of Birth",
                  fieldName: "dateOfBirth",
                  fieldType: "DATE_RANGE",
                },
                {
                  title: "Employment Date",
                  fieldName: "employmentDate",
                  fieldType: "DATE_RANGE",
                },
                {
                  title: "Termination Date",
                  fieldName: "terminationDate",
                  fieldType: "DATE_RANGE",
                },
                {
                  title: "Date Created",
                  fieldName: "createdAt",
                  fieldType: "DATE_RANGE",
                },
                {
                  title: "Resident",
                  fieldName: "resident",
                  fieldType: "YES_NO",
                },
                {
                  title: "Reemployed",
                  fieldName: "reemployed",
                  fieldType: "YES_NO",
                },
                {
                  title: "Union Member",
                  fieldName: "unionMember",
                  fieldType: "YES_NO",
                },
              ]}
            />
            <ManageTableColumns
              columns={tanstackEmployeeColumns}
              defaultVisibilityModel={defaultColumnVisibility}
              visibilityModel={columnVisibilityModel}
              setVisibilityModel={setColumnVisibilityModel}
              tableApiRef={tableAPIRef}
            />
          </div>
          <div className="flex items-center gap-4 font-medium">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
              >
                <Upload className="mr-3 text-[17px] text-gray-500" />
                Upload/Export
                <ChevronDown
                  className={`ml-2 text-[14px] text-gray-500 transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isOpen && (
                <div className="absolute top-full left-0 mt-1 w-full min-w-[160px] bg-white border border-gray-300 rounded-lg shadow-lg z-50">
                  <div className="py-1">
                    <PermissionChecker
                      permissionRule="OR"
                      permissions={[
                        PERMISSIONS.EMPLOYEES_CREATE,
                        PERMISSIONS.EMPLOYEES_WRITE,
                      ]}
                    >
                      <button
                        onClick={() =>
                          handleCreateEmployeeForm(setOpenUploadEmployeesModal)
                        }
                        className="w-full text-left px-3 py-2 text-[12px] text-gray-600 hover:bg-gray-50 flex items-center"
                      >
                        <Upload className="mr-3 text-[17px] text-gray-500" />
                        Upload Employee
                      </button>
                    </PermissionChecker>

                    <PermissionChecker
                      permissionRule="OR"
                      permissions={[
                        PERMISSIONS.SALARY_CREATE,
                        PERMISSIONS.SALARY_WRITE,
                      ]}
                    >
                      <button
                        onClick={() =>
                          handleCreateSalaryForm(setOpenUploadSalaryModal)
                        }
                        className="w-full text-left px-3 py-2 text-[12px] text-gray-600 hover:bg-gray-50 flex items-center"
                      >
                        <Upload className="mr-3 text-[17px] text-gray-500" />
                        Upload Salary
                      </button>
                    </PermissionChecker>
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        setOpenExportEmployeesModal(true);
                      }}
                      className="w-full text-left px-3 py-2 text-[12px] text-gray-600 hover:bg-gray-50 flex items-center"
                    >
                      <BsDownload className="mr-3 text-[17px] text-gray-500" />
                      Export Employees
                    </button>
                  </div>
                </div>
              )}
            </div>
            <PermissionChecker
              permissionRule="OR"
              permissions={[
                PERMISSIONS.EMPLOYEES_CREATE,
                PERMISSIONS.EMPLOYEES_WRITE,
              ]}
            >
              <button
                onClick={() =>
                  handleCreateEmployeeForm(setOpenAddEmployeeModal)
                }
                disabled={checkingEmployeeLimit}
                className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white disabled:opacity-50"
              >
                <AiOutlinePlus className="mr-2 text-lg" />
                Add New Employee
              </button>
            </PermissionChecker>
          </div>
        </div>
        <TanStackEmployeeTable
          ref={tableAPIRef}
          tableId={TableId.EMPLOYEES}
          data={tableData}
          columns={tanstackEmployeeColumns}
          columnVisibility={columnVisibilityModel}
          onColumnVisibilityChange={setColumnVisibilityModel}
          loading={isLoading}
          onRowClick={(row, event) => {
            if (checkUserPermissionsAnd([PERMISSIONS.EMPLOYEES_WRITE])) {
              setFormData(row);
              setAnchorEl(event.currentTarget);
            }
          }}
          onActionClick={(row, event) => {
            setFormData(row);
            setAnchorEl(event.currentTarget);
          }}
          initialColumnVisibility={defaultColumnVisibility}
          pageSize={10}
          pageCount={pageCount}
          apiRef={tableAPIRef}
        />
        <ActionPopover
          id={anchorEl ? "simple-popover" : undefined}
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          handleClose={handleClose}
          setOpenDeleteModal={() => {}}
          doNotShowDeleteButton={true}
          setOpenEdit={setOpenEditEmployeeModal}
          setOpenModal={setOpenEditEmployeeModal}
          additionalOptions={
            <>
              {currentCompany?.enableEmployeeLogin && (
                <div
                  onClick={() => {
                    setOpenResendConfirmationModal(true);
                    handleClose();
                  }}
                  className="table-action-popover"
                >
                  <BiRecycle />
                  <p className="text-2sm">Resend Account Confirmation Email</p>
                </div>
              )}
              {formDataValues?.status === "ACTIVE" && (
                <div
                  onClick={() => {
                    setOpenTerminateEmployeeModal(true);
                    handleClose();
                  }}
                  className="table-action-popover"
                >
                  <RiIndeterminateCircleLine />
                  <p className="text-2sm">Terminate</p>
                </div>
              )}
              {formDataValues?.status === "TERMINATED" && (
                <div
                  onClick={() => {
                    setOpenReinstateEmployeeModal(true);
                    handleClose();
                  }}
                  className="table-action-popover"
                >
                  <MdOutlineRestoreFromTrash />
                  <p className="text-2sm">Reinstate</p>
                </div>
              )}
            </>
          }
        />

        {openAddEmployeeModal && (
          <AddNewEmployee
            open={openAddEmployeeModal}
            setOpen={setOpenAddEmployeeModal}
          />
        )}

        {openEditEmployeeModal && (
          <EditEmployee
            open={openEditEmployeeModal}
            setOpen={setOpenEditEmployeeModal}
            tableAPIRef={tableAPIRef}
            reloadTableData={reloadTableData}
          />
        )}
      </EmployeeFormContextWrapper>

      {openUploadEmployeesModal && (
        <UploadEmployees
          open={openUploadEmployeesModal}
          setOpen={setOpenUploadEmployeesModal}
        />
      )}

      {openUploadSalaryModal && (
        <UploadSalary
          open={openUploadSalaryModal}
          setOpen={setOpenUploadSalaryModal}
        />
      )}

      {openResendConfirmationModal && (
        <ResendConfirmation
          open={openResendConfirmationModal}
          setOpen={setOpenResendConfirmationModal}
        />
      )}

      {openTerminateEmployeeModal && (
        <TerminateEmployee
          open={openTerminateEmployeeModal}
          setOpen={setOpenTerminateEmployeeModal}
          tableAPIRef={tableAPIRef}
        />
      )}

      {openReinstateEmployeeModal && (
        <ReinstateEmployee
          open={openReinstateEmployeeModal}
          setOpen={setOpenReinstateEmployeeModal}
          reloadTableData={reloadTableData}
        />
      )}

      {openExportEmployeesModal && currentCompany?.id && (
        <ExportEmployeesModal
          open={openExportEmployeesModal}
          setOpen={setOpenExportEmployeesModal}
          companyId={currentCompany.id}
        />
      )}

      <UpdateAccountConfigModal
        open={openUpgradeModal}
        content={upgradeModalContent}
        handleClose={() => setOpenUpgradeModal(false)}
        onClickActionButton={onClickActionButton}
      />
    </>
  );
};

export default EmployeesMainContent;
